import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  BookingTaxEstimateBodyInterface,
  BookingTaxEstimateInterface,
} from '@sport-activities/sdk';
import { Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { UserService } from '~/auth/services/user/user.service';
import { CheckoutItem } from '~/checkout/model/checkout-item.model';
import { ACTIVITIES_BASE_URL } from '~/core/core.module';
import { getCountryFromLocale } from '~/core/helpers/locale/locale.helper';
import { Subscription } from '~/attendee/model/subscription.model';
import { splitByAge } from '~/core/helpers/attendees/split-by-age.helper';
import { AgeRequirementService } from '~/core/services/activity/age-requirement.service';

@Injectable({
  providedIn: 'root',
})
export class TaxEstimateService {
  private readonly taxEstimateUrl: string;

  constructor(
    @Inject(ACTIVITIES_BASE_URL) private readonly baseUrl: string,
    private readonly translateService: TranslateService,
    @Inject(LOCALE_ID) private readonly locale: string,
    private readonly userService: UserService,
    private readonly http: HttpClient,
    private readonly ageRequirement: AgeRequirementService
  ) {
    this.taxEstimateUrl = `${this.baseUrl}/booking/tax-estimate`;
  }

  private buildPayload(
    checkoutItems: CheckoutItem[],
    countryCode: string
  ): Observable<BookingTaxEstimateBodyInterface> {
    const payload = {
      originCountry: countryCode,
      details: [],
    };

    payload.details = checkoutItems.map((checkoutItem) => {
      const { timeslot, numberOfAttendees } = checkoutItem;
      const offer = timeslot.offers.find(
        (o) => o.identifier === checkoutItem.defaultOffer
      );

      return {
        sku: timeslot.identifier,
        offers: [
          {
            identifier: offer.identifier,
            quantity: numberOfAttendees || 1,
          },
        ],
      };
    });

    return of(payload);
  }

  public getTaxes(
    items: CheckoutItem[] | Subscription
  ): Observable<BookingTaxEstimateInterface> {
    const checkoutItems =
      items instanceof Subscription ? this.toCheckoutItems(items) : items;

    if (!checkoutItems?.filter((item) => item.totalPrice > 0).length) {
      return of(null);
    }

    return this.userService.isAuthenticated().pipe(
      mergeMap((isAuthenticated) => {
        let countryCode =
          getCountryFromLocale(this.translateService.currentLang) ||
          this.locale;

        if (isAuthenticated) {
          countryCode = this.userService.getUser().country;
        }

        return this.buildPayload(checkoutItems, countryCode);
      }),
      mergeMap((payload) => {
        return this.http.post<BookingTaxEstimateInterface>(
          this.taxEstimateUrl,
          payload
        );
      })
    );
  }

  private toCheckoutItems(subscription: Subscription): CheckoutItem[] {
    const attendees = splitByAge(
      subscription.item.attendees,
      this.ageRequirement.tenantMajority
    );
    const checkoutItem = new CheckoutItem({
      activity: subscription.activity,
      timeslot: subscription.timeslot,
      adults: attendees.adults.map((attendee) => ({
        age: attendee.age,
        firstname: attendee.name,
        lastname: attendee.name,
      })),
      children: attendees.children.map((attendee) => ({
        age: attendee.age,
        firstname: attendee.name,
        lastname: attendee.name,
      })),
      defaultOffer: subscription.item.offerIdentifier,
    });

    return [checkoutItem];
  }
}
